import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  HandHeart,
  HeartHandshake,
  Quote,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import SocialCarousel from '../components/SocialCarousel';
import { organization, programs } from '../data/siteData';
import { assetPath } from '../utils/assetPath';

function ProgramCard({ program, index }) {
  const Icon = program.icon;
  const banner = program.id === 'becas'
    ? assetPath('/assets/banners/Leaderboard BECAS.png')
    : program.id === 'valores'
      ? assetPath('/assets/banners/Leaderboard SE TU AMIGO.png')
      : null;

  return (
    <Reveal className={`program-card program-card--${program.theme}`} delay={index * 0.08}>
      <Link to={program.path} aria-label={`Conocer ${program.title}`}>
        <div className="program-card__visual">
          <img className="program-card__photo" src={program.image} alt="" loading="lazy" />
          <div className="program-card__wash" />
          <span className="program-card__icon"><Icon /></span>
        </div>
        {banner && <img className="program-card__banner" src={banner} alt="" loading="lazy" />}
        <div className="program-card__body">
          <span className="program-card__eyebrow">{program.eyebrow}</span>
          <h3>{program.title}</h3>
          <p>{program.intro}</p>
          <span className="text-link">Conoce el programa <ArrowRight size={18} /></span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'NGO',
    name: organization.name,
    url: import.meta.env.VITE_SITE_URL || 'https://fundaciontony.com.mx',
    logo: `${import.meta.env.VITE_SITE_URL || 'https://fundaciontony.com.mx'}/assets/brand/logo-fundacion-square.png`,
    foundingDate: '1995',
    description: 'Organización que impulsa educación, valores y prevención de adicciones en México.',
    telephone: organization.phone,
    areaServed: 'México',
    sameAs: [organization.facebook, organization.instagram],
    knowsAbout: ['Educación superior', 'Formación en valores', 'Prevención de adicciones', 'Rehabilitación'],
  };

  return (
    <main id="contenido">
      <SEO schema={organizationSchema} />

      <section className="hero" ref={heroRef}>
        <div className="hero__mesh" aria-hidden="true">
          <motion.span animate={{ x: [0, 32, 0], y: [0, -22, 0] }} transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }} />
          <motion.span animate={{ x: [0, -25, 0], y: [0, 26, 0] }} transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }} />
        </div>
        <div className="shell hero__layout">
          <motion.div className="hero__copy" style={{ y: textY }}>
            <motion.span className="eyebrow eyebrow--light" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              Compromiso que se convierte en acción
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}>
              Transformamos vidas.<br /><em>Construimos futuro.</em>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}>
              Generamos oportunidades a través de la educación, la formación en valores y la prevención de adicciones.
            </motion.p>
            <motion.div className="hero__actions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}>
              <a className="button button--light" href="#programas">Conoce nuestros programas <ArrowRight /></a>
              <Link className="button button--ghost-light" to="/contacto">Súmate al cambio</Link>
            </motion.div>
          </motion.div>

          <motion.div className="hero__visual" style={{ y: imageY }}>
            <div className="hero__photo-frame">
              <img src={assetPath('/assets/photos/503863425_3056882627808473_8620227016567074574_n.jpg')} alt="Integrantes de Fundación Tony reunidos en una actividad comunitaria" />
            </div>
            <motion.div className="hero__stamp" animate={{ rotate: [0, 4, 0, -4, 0] }} transition={{ duration: 9, repeat: Infinity }}>
              <strong>31</strong><span>años de<br />impacto</span>
            </motion.div>
            <div className="hero__mini-card">
              <span>Desde 1995</span>
              <strong>Una oportunidad puede cambiar una historia.</strong>
            </div>
          </motion.div>
        </div>
        <a className="hero__scroll" href="#nosotros"><ArrowDown /> Descubre nuestra historia</a>
      </section>

      <section className="intro section" id="nosotros">
        <div className="shell intro__grid">
          <Reveal className="intro__statement">
            <span className="eyebrow eyebrow--blue">Quiénes somos</span>
            <h2>Nacimos de una idea sencilla: el éxito también debe compartirse.</h2>
          </Reveal>
          <Reveal className="intro__copy" delay={0.12}>
            <p>Somos la expresión del compromiso social de Grupo Tony. Trabajamos frente a barreras concretas: jóvenes que interrumpen sus estudios, familias afectadas por las adicciones y comunidades que necesitan más herramientas para desarrollarse.</p>
            <p>Cuando una persona encuentra una oportunidad, el cambio no termina en ella: alcanza a su familia, fortalece a su comunidad y abre posibilidades para quienes vienen detrás.</p>
            <a className="text-link" href="#proposito">Nuestro propósito <ArrowRight size={18} /></a>
          </Reveal>
        </div>

        <div className="shell values-grid" id="proposito">
          <Reveal className="value-card value-card--blue">
            <span><HandHeart /></span>
            <p>Misión</p>
            <h3>Disminuir brechas con programas de educación, valores, prevención y colaboración social.</h3>
          </Reveal>
          <Reveal className="value-card value-card--green" delay={0.08}>
            <span><Sparkles /></span>
            <p>Visión</p>
            <h3>Crecer con profundidad: más alcance, mejores alianzas y un impacto social sostenible.</h3>
          </Reveal>
          <Reveal className="value-photo" delay={0.16}>
            <img src={assetPath('/assets/photos/504358165_3058480867648649_6609700657505944187_n.jpg')} alt="Equipo de Fundación Tony durante una jornada" loading="lazy" />
            <div><Quote /><p>El verdadero cambio comienza cuando alguien encuentra una oportunidad y alguien más decide creer en ella.</p></div>
          </Reveal>
        </div>
      </section>

      <section className="programs-section section" id="programas">
        <div className="programs-section__orb" aria-hidden="true" />
        <div className="shell">
          <Reveal className="section-heading section-heading--split">
            <div>
              <span className="eyebrow eyebrow--light">Tres caminos, un mismo propósito</span>
              <h2>Programas que abren oportunidades</h2>
            </div>
            <p>Cada iniciativa responde a una realidad concreta y acompaña a personas en momentos decisivos de su vida.</p>
          </Reveal>
          <div className="program-grid">
            {programs.map((program, index) => <ProgramCard key={program.id} program={program} index={index} />)}
          </div>
        </div>
      </section>

      <section className="impact section">
        <div className="shell impact__grid">
          <Reveal className="impact__visual">
            <img src={assetPath('/assets/photos/504879512_3058824290947640_7799668488919146091_n.jpg')} alt="Participantes en una actividad formativa de Fundación Tony" loading="lazy" />
            <div className="impact__badge"><Users /><strong>Miles</strong><span>de personas beneficiadas directa e indirectamente</span></div>
          </Reveal>
          <div className="impact__content">
            <Reveal>
              <span className="eyebrow eyebrow--blue">Impacto que permanece</span>
              <h2>No contamos apoyos. Contamos historias que pudieron continuar.</h2>
              <p>El impacto vive en un estudiante que permanece en la universidad, una familia que recibe orientación a tiempo, una persona que inicia un tratamiento y un docente que lleva nuevas herramientas a su comunidad.</p>
            </Reveal>
            <div className="impact__pillars">
              <Reveal className="impact-pillar" delay={0.08}><BookOpen /><div><strong>Educación</strong><span>Continuidad y excelencia</span></div></Reveal>
              <Reveal className="impact-pillar" delay={0.14}><ShieldCheck /><div><strong>Prevención</strong><span>Decisiones informadas</span></div></Reveal>
              <Reveal className="impact-pillar" delay={0.2}><HeartHandshake /><div><strong>Comunidad</strong><span>Alianzas que multiplican</span></div></Reveal>
            </div>
          </div>
        </div>
      </section>

      <SocialCarousel />

      <section className="closing-cta">
        <div className="closing-cta__photo" aria-hidden="true" />
        <div className="closing-cta__overlay" />
        <Reveal className="shell closing-cta__content">
          <span className="eyebrow eyebrow--light">El cambio necesita aliados</span>
          <h2>Una oportunidad puede transformar generaciones.</h2>
          <p>Conoce los programas, comparte nuestro trabajo o conversemos sobre cómo sumar esfuerzos.</p>
          <Link className="button button--light" to="/contacto">Quiero ser parte <ArrowRight /></Link>
        </Reveal>
      </section>
    </main>
  );
}
